import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Calendar, ChevronDown, Clock, ArrowLeft } from 'lucide-react';
import { Language } from '../App';
import { PageHero } from '../components/PageHero';
interface ArchiveProps {
  language: Language;
}
interface Issue {
  month: string;
  monthPs: string;
  year: number;
  vol: number;
  issue: number;
  cover?: string;
}
const archiveIssues: Issue[] = [{
  month: 'Jan-Feb',
  monthPs: 'جنوري – فبروري',
  year: 2026,
  vol: 21,
  issue: 1,
  cover: 'https://cdn.magicpatterns.com/uploads/nkxSFVNX8PortnXbKmnDDH/image.png'
}, {
  month: 'May',
  monthPs: 'مۍ',
  year: 2016,
  vol: 11,
  issue: 3,
  cover: 'https://cdn.magicpatterns.com/uploads/hu3f7kA4Jr17svFD69LdG5/May_2016_Vol_11_Issu_3.jpg'
}, {
  month: 'Mar',
  monthPs: 'مارچ',
  year: 2016,
  vol: 11,
  issue: 2,
  cover: 'https://cdn.magicpatterns.com/uploads/vJrrSkrMYLWKFP6AvYUrgB/Mar_2016_Vol_11,_iss_2.jpg'
}, {
  month: 'Jan',
  monthPs: 'جنوري',
  year: 2016,
  vol: 11,
  issue: 1,
  cover: 'https://cdn.magicpatterns.com/uploads/8Kd2Nfw8A3t6VnDouWf6wH/Jan_2016_Vol_11_Iss_1.jpg'
}, {
  month: 'Nov',
  monthPs: 'نومبر',
  year: 2015,
  vol: 10,
  issue: 6,
  cover: 'https://cdn.magicpatterns.com/uploads/mifZ8dMtsjvgnCPVB3k5W1/Nov_2015_Vol_10,_iss_6.jpg'
}, {
  month: 'Sep',
  monthPs: 'سپتمبر',
  year: 2015,
  vol: 10,
  issue: 5
}, {
  month: 'Jul',
  monthPs: 'جولای',
  year: 2015,
  vol: 10,
  issue: 4
}, {
  month: 'May',
  monthPs: 'مۍ',
  year: 2015,
  vol: 10,
  issue: 3
}, {
  month: 'Mar',
  monthPs: 'مارچ',
  year: 2015,
  vol: 10,
  issue: 2
}, {
  month: 'Jan',
  monthPs: 'جنوري',
  year: 2015,
  vol: 10,
  issue: 1
}];
const allYears = [2026, 2016, 2015, 2014, 2013, 2012, 2011, 2010, 2009, 2008, 2007, 2006, 2005];
// Years that have uploaded issues
const availableYears = [2026, 2016, 2015];
export function Archive({
  language
}: ArchiveProps) {
  const isPashto = language === 'pashto';
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const getIssuesForYear = (year: number) => archiveIssues.filter(i => i.year === year);
  const isYearAvailable = (year: number) => availableYears.includes(year);
  const getVolumeForYear = (year: number) => {
    const issues = getIssuesForYear(year);
    if (issues.length > 0) return issues[0].vol;
    // Calculate volume based on year (Vol 1 = 2006, started Dec 2005)
    if (year === 2005) return 1;
    return year - 2005;
  };
  const getIssueCountForYear = (year: number) => {
    const issues = getIssuesForYear(year);
    if (issues.length > 0) return issues.length;
    return year === 2005 ? 1 : 6;
  };
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-50px' as const
    },
    transition: {
      duration: 0.5
    }
  };
  // Detail view for a selected year
  if (selectedYear !== null) {
    const yearAvailable = isYearAvailable(selectedYear);
    const yearIssues = getIssuesForYear(selectedYear);
    return <div className="pt-24 md:pt-28 bg-gray-50 min-h-screen pb-20" data-id="element-393">
        <PageHero language={language} titleEn="Archive Library" titlePs="د آرشیف کتابتون" introEn="Browse past editions" introPs="تیرې ګڼې وپلټئ" data-id="element-394" />

        <section className="py-12 px-6" data-id="element-395">
          <div className="max-w-5xl mx-auto" data-id="element-396">
            {/* Back button */}
            <button onClick={() => setSelectedYear(null)} className={`flex items-center gap-2 text-pasoon-red hover:text-red-800 font-medium mb-8 transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-397">
              <ArrowLeft className="w-4 h-4" data-id="element-398" />
              {isPashto ? 'بیرته آرشیف ته' : 'Back to Archive'}
            </button>

            {/* Year heading */}
            <h2 className={`text-4xl md:text-5xl font-bold text-pasoon-dark mb-2 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-399">
              {selectedYear}
            </h2>
            <p className={`text-gray-500 mb-10 ${isPashto ? 'font-pashto' : ''}`} data-id="element-400">
              {isPashto ? `ټوک ${getVolumeForYear(selectedYear)} — ${getIssueCountForYear(selectedYear)} ګڼې` : `Volume ${getVolumeForYear(selectedYear)} — ${getIssueCountForYear(selectedYear)} Issues`}
            </p>

            {yearAvailable ?
          // Show actual issues
          <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-id="element-401">
                {yearIssues.map((issue, idx) => <motion.div key={`${issue.month}-${issue.year}`} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.08
            }} className="group cursor-pointer" data-id="element-402">
                    <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 hover:shadow-lg transition-shadow" data-id="element-403">
                      {issue.cover ? <div className="p-2 bg-white flex items-center justify-center" data-id="element-404">
                          <img src={issue.cover} alt={`Pasoon ${issue.month} ${issue.year}`} className="w-full h-auto object-contain rounded-sm" loading="eager" data-id="element-405" />
                        </div> : <div className="aspect-[3/4] bg-gradient-to-br from-red-800 to-red-950 flex flex-col items-center justify-center relative" data-id="element-406">
                          <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_30%_20%,_white_0%,_transparent_50%)]" data-id="element-407"></div>
                          <div className="relative z-10 border border-white/15 rounded px-3 py-5 w-4/5 flex flex-col items-center text-center" data-id="element-408">
                            <h3 className="font-serif text-lg font-bold text-white mb-1.5" data-id="element-409">
                              Pasoon
                            </h3>
                            <div className="w-6 h-0.5 bg-white/25 mb-2" data-id="element-410"></div>
                            <p className={`text-white/80 text-xs font-medium ${isPashto ? 'font-pashto' : 'uppercase tracking-wider'}`} data-id="element-411">
                              {isPashto ? issue.monthPs : issue.month}{' '}
                              {issue.year}
                            </p>
                          </div>
                        </div>}
                      <div className="p-4 text-start" data-id="element-412">
                        <p className={`font-medium text-pasoon-dark text-sm ${isPashto ? 'font-pashto' : ''}`} data-id="element-413">
                          {isPashto ? `${issue.monthPs} ${issue.year}` : `${issue.month} ${issue.year}`}
                        </p>
                        <p className={`text-gray-400 text-xs mt-0.5 ${isPashto ? 'font-pashto' : ''}`} data-id="element-414">
                          {isPashto ? `ټوک ${issue.vol}، ګڼه ${issue.issue}` : `Vol ${issue.vol}, Iss ${issue.issue}`}
                        </p>
                      </div>
                    </div>
                  </motion.div>)}
              </motion.div> :
          // Placeholder - Archive Coming Soon
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="max-w-lg mx-auto text-center py-20" data-id="element-415">
                <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6" data-id="element-416">
                  <Clock className="w-9 h-9 text-gray-400" data-id="element-417" />
                </div>
                <h3 className={`text-2xl md:text-3xl font-bold text-pasoon-dark mb-4 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-418">
                  {isPashto ? 'آرشیف ډیر ژر' : 'Archive Coming Soon'}
                </h3>
                <p className={`text-gray-500 text-lg mb-8 ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-419">
                  {isPashto ? 'موږ اوس مهال تاریخي ګڼې اپلوډ کوو. مهرباني وکړئ ډیر ژر بیا وګورئ.' : 'We are currently uploading historical issues. Please check back soon.'}
                </p>
                <button onClick={() => setSelectedYear(null)} className={`inline-flex items-center gap-2 px-6 py-3 bg-pasoon-red text-white rounded-xl font-medium hover:bg-red-800 transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-420">
                  <ArrowLeft className="w-4 h-4" data-id="element-421" />
                  {isPashto ? 'بیرته آرشیف ته' : 'Back to Archive'}
                </button>
              </motion.div>}
          </div>
        </section>
      </div>;
  }
  // Main archive listing
  return <div className="pt-24 md:pt-28 bg-gray-50 min-h-screen pb-20" data-id="element-422">
      <PageHero language={language} titleEn="Archive Library" titlePs="د آرشیف کتابتون" introEn="Browse past editions" introPs="تیرې ګڼې وپلټئ" data-id="element-423" />

      {/* Intro */}
      <section className="py-8 px-6" data-id="element-424">
        <div className="max-w-3xl mx-auto text-center" data-id="element-425">
          <motion.div {...fadeIn} data-id="element-426">
            <p className={`text-gray-600 ${isPashto ? 'font-pashto leading-loose text-lg' : 'text-lg leading-relaxed'}`} data-id="element-427">
              {isPashto ? 'زموږ آرشیف د ۲۰۰۵ راهیسې تر اوسه خپرې شوې ګڼې لري. هره ګڼه د پښتو ادب، شاعرۍ او علمي فکر وده منعکسوي.' : 'Our archive contains editions published from 2005 to present. Each issue reflects the growth of Pashto literature, poetry, and academic thought.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Archive by Year */}
      <section className="py-8 px-6" data-id="element-428">
        <div className="max-w-4xl mx-auto" data-id="element-429">
          <div className="space-y-4" data-id="element-430">
            {allYears.map((year, idx) => {
            const available = isYearAvailable(year);
            const vol = getVolumeForYear(year);
            const issueCount = getIssueCountForYear(year);
            return <motion.div key={year} initial={{
              opacity: 0,
              y: 15
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.05,
              duration: 0.4
            }} className={`rounded-2xl border overflow-hidden transition-all ${available ? 'bg-white border-gray-200 shadow-sm hover:shadow-md' : 'bg-gray-50 border-dashed border-gray-200'}`} data-id="element-431">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 gap-4" data-id="element-432">
                    <div className="flex items-center gap-4" data-id="element-433">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${available ? 'bg-pasoon-red/10' : 'bg-gray-100'}`} data-id="element-434">
                        <Calendar className={`w-5 h-5 ${available ? 'text-pasoon-red' : 'text-gray-400'}`} data-id="element-435" />
                      </div>
                      <div data-id="element-436">
                        <h3 className={`text-2xl font-bold ${available ? 'text-pasoon-dark' : 'text-gray-400'} ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-437">
                          {year}
                        </h3>
                        <p className={`text-sm ${available ? 'text-gray-500' : 'text-gray-400'} ${isPashto ? 'font-pashto' : ''}`} data-id="element-438">
                          {isPashto ? `ټوک ${vol} — ${issueCount} ګڼې` : `Vol ${vol} — ${issueCount} Issues`}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3" data-id="element-439">
                      {available ? <>
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 text-pasoon-muted text-xs font-bold ${isPashto ? 'font-pashto' : 'uppercase tracking-wider'}`} data-id="element-440">
                            <BookOpen className="w-3.5 h-3.5" data-id="element-441" />
                            {isPashto ? 'د آرشیف ګڼې' : 'Archive Issues'}
                          </span>
                          <button onClick={() => setSelectedYear(year)} className={`px-5 py-2.5 bg-pasoon-red text-white rounded-xl text-sm font-medium hover:bg-red-800 transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-442">
                            {isPashto ? 'آرشیف وګورئ' : 'View Archive'}
                          </button>
                        </> : <>
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-gray-500 text-xs font-bold ${isPashto ? 'font-pashto' : 'uppercase tracking-wider'}`} data-id="element-443">
                            <Clock className="w-3.5 h-3.5" data-id="element-444" />
                            {isPashto ? 'د آرشیف ګڼې' : 'Archive Issues'}
                          </span>
                          <button onClick={() => setSelectedYear(year)} className={`px-5 py-2.5 bg-gray-200 text-gray-500 rounded-xl text-sm font-medium hover:bg-gray-300 transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-445">
                            {isPashto ? 'آرشیف وګورئ' : 'View Archive'}
                          </button>
                        </>}
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Bottom note */}
      <section className="py-6 px-6 text-center" data-id="element-446">
        <motion.div {...fadeIn} data-id="element-447">
          <div className="flex items-center justify-center gap-2 text-pasoon-red mb-2" data-id="element-448">
            <BookOpen className="w-5 h-5" data-id="element-449" />
            <span className={`font-medium ${isPashto ? 'font-pashto' : ''}`} data-id="element-450">
              {isPashto ? 'د ۲۰۰۵ دسمبر — تر اوسه' : 'December 2005 — Present'}
            </span>
          </div>
          <p className={`text-gray-500 text-sm ${isPashto ? 'font-pashto' : ''}`} data-id="element-451">
            {isPashto ? 'د پښتو ژبې بډایتوب د مطالعې، لوستلو او ساتلو لپاره تیرو ګڼو ته لاسرسی ومومئ.' : 'Access past issues to study, read, and preserve the richness of Pashto language.'}
          </p>
        </motion.div>
      </section>
    </div>;
}